import { RoutePath } from '@app/routes';
import { Icon, IconProp } from '@components/Icon';
import { Link } from '@components/Link';
import { Logo } from '@components/Logo';
import { Tooltip } from '@components/Tooltip';
import { useFetchCustomerList } from '@connect-carrier/hooks/useFetchCustomerList';
import {
  Roles,
  useHasAnyRole,
  useHasRole,
} from '@connect-carrier/hooks/useHasRole';
import { useHeaderScreenSizes } from '@connect-carrier/hooks/useHeaderScreenSizes';
import { useTendersCount } from '@connect-carrier/hooks/useTendersCount';
import { ConnectCarrierRoutePath } from '@connect-carrier/routes/defs';
import { GlobalSearch } from '@connect-carrier/views/customer-landing/header/GlobalSearch';
import { useTheme } from '@emotion/react';
import { useFlagMe400205CarrierPortalTendersFrontend } from '@generated/flags/ME-400205-carrier-portal-tenders-frontend';
import { useFlagMe419583EnableCarrierPortalResponsiveness } from '@generated/flags/ME-419583-enable-carrier-portal-responsiveness';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { useFlagMe486626CarrierPortalAccountingQueueTabFe } from '@generated/flags/ME-486626-carrier-portal-accounting-queue-tab-fe';
import { useFlagMe490256CarrierFilterButtonsResponsivenessFix } from '@generated/flags/ME-490256-carrier-filter-buttons-responsiveness-fix';
import { HEADER_HEIGHT } from '@utils/constants';
import { win } from '@utils/win';
import { APP_HEADER } from '@utils/zIndex';
import { compact, isEmpty } from 'lodash-es';
import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useWindowSize } from 'react-use';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

interface NavItem {
  label: string;
  path: ConnectCarrierRoutePath<string, string>;
  testId: string;
  icon: IconProp;
}

interface SidebarProps {
  isCollapsed: boolean;
  onClose?: () => void;
}

// ts-unused-exports:disable-next-line
export const Sidebar: FC<SidebarProps> = ({ isCollapsed, onClose }) => {
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  const enableCarrierPortalResponsiveness =
    useFlagMe419583EnableCarrierPortalResponsiveness();
  const enableFilterButtonResponsivenessFix =
    useFlagMe490256CarrierFilterButtonsResponsivenessFix();
  const { colors, gray } = useTheme();
  const { mobile } = useHeaderScreenSizes();
  const { width } = useWindowSize();
  const sidebarBg = gray[99];
  const textColor = gray[30];
  const activeBg = colors.primaryLight;
  const activeTextColor = colors.primary;
  const hoverBg = gray[95];
  const borderColor = gray[80];
  const mobileResp = width <= 360;
  const tabletResp = width >= 361 && width <= 1300;
  const desktopResp = width >= 1301;

  const location = useLocation();
  const fromPage = location.state?.fromPage as string | undefined;
  const showSearchBar = location?.pathname !== '/';

  const isAdmin = useHasRole(Roles.Admin);
  const isReadOnly = useHasRole(Roles.ReadOnly);
  const isPricing = useHasRole(Roles.Pricing);
  const isAccountingTabEnabled =
    useFlagMe486626CarrierPortalAccountingQueueTabFe();
  const exceptPricingRole = useHasAnyRole([
    Roles.Admin,
    Roles.Accounting,
    Roles.Operations,
    Roles.ReadOnly,
  ]);
  const canViewOffers = isAdmin || isReadOnly || isPricing;
  const isCarrierPortalTendersEnabled =
    useFlagMe400205CarrierPortalTendersFrontend();
  const tendersCount = useTendersCount();
  const { customerList } = useFetchCustomerList();

  if (!enableSidebarNavigation) {
    return null;
  }

  const isActiveLink = (linkPath: string): boolean => {
    return (
      location.pathname === linkPath ||
      (linkPath === '/available-routes' &&
        location.pathname.includes('/available-route-details')) ||
      (linkPath === '/offers' &&
        location.pathname.includes('/offer-details')) ||
      (linkPath === '/offers' &&
        fromPage === 'offers' &&
        location.pathname.includes('/submit-offer')) ||
      (linkPath === '/available-routes' &&
        fromPage === 'available-routes' &&
        location.pathname.includes('/submit-offer')) ||
      (linkPath === '/accounting' &&
        location.pathname.includes('/accounting-route-details')) ||
      (linkPath === '/my-routes' &&
        location.pathname.includes('/route-details')) ||
      (linkPath === '/tenders' &&
        location.pathname.includes('/tender-details')) ||
      (linkPath === '/my-routes' &&
        location.pathname.includes('/driver') &&
        !location.pathname.includes('/accounting-route-details')) ||
      (linkPath === '/user-management' &&
        location.pathname.includes('/user-details'))
    );
  };

  // Build nav links with a FIXED display order.
  // Each item is shown/hidden by a visibility flag; the order never changes.
  const hasCustomerContext = customerList.length > 0;
  // Pricing-only role = pricing WITHOUT any of the "exception" roles
  const onlyPricingRole = isPricing && !exceptPricingRole;

  const links: NavItem[] = !showSearchBar
    ? compact([
        isAdmin
          ? {
              label: 'User Management',
              path: '/user-management' as const,
              testId: 'sidebar-link-user-management',
              icon: 'user' as const,
            }
          : false,
      ])
    : (
        [
          // 1. My Routes — hidden for pricing-only users
          hasCustomerContext && !onlyPricingRole
            ? {
                label: 'My Routes',
                path: '/my-routes' as const,
                testId: 'sidebar-link-my-routes',
                icon: 'route' as const,
              }
            : false,

          // 2. Available Routes — admin, readOnly, or any pricing combo
          hasCustomerContext && (isAdmin || isReadOnly || isPricing)
            ? {
                label: 'Available Routes',
                path: '/available-routes' as const,
                testId: 'sidebar-link-available-routes',
                icon: 'road' as const,
              }
            : false,

          // 3. Offers
          hasCustomerContext && canViewOffers
            ? {
                label: 'Offers',
                path: '/offers' as const,
                testId: 'sidebar-link-offers',
                icon: 'tags' as const,
              }
            : false,

          // 4. Tenders (flag-gated)
          hasCustomerContext &&
          isCarrierPortalTendersEnabled &&
          (isPricing || exceptPricingRole)
            ? {
                label: `Tenders (${tendersCount.count})`,
                path: '/tenders' as const,
                testId: 'sidebar-link-tenders',
                icon: 'fileLines' as const,
              }
            : false,

          // 5. Accounting (flag-gated, accounting role only)
          hasCustomerContext && isAccountingTabEnabled
            ? {
                label: 'Accounting',
                path: '/accounting' as const,
                testId: 'sidebar-link-accounting',
                icon: 'dollarSign' as const,
              }
            : false,

          // 6. User Management — admin only, no customer context required
          isAdmin
            ? {
                label: 'User Management',
                path: '/user-management' as const,
                testId: 'sidebar-link-user-management',
                icon: 'user' as const,
              }
            : false,
        ] as (NavItem | false)[]
      ).filter((item): item is NavItem => Boolean(item));

  return (
    <nav
      data-testid="sidebar-nav"
      css={{
        // Desktop (> 1300px): inline flex item, fixed height so branding stays pinned
        width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        height: '100%',
        backgroundColor: sidebarBg,
        borderRight: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.25s ease',
        // Mobile / tablet (≤ 1300px): fixed overlay drawer
        '@media (max-width: 1300px)': {
          position: 'fixed',
          top: HEADER_HEIGHT,
          left: isCollapsed ? -EXPANDED_WIDTH : 0,
          width: EXPANDED_WIDTH,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          zIndex: enableFilterButtonResponsivenessFix
            ? APP_HEADER + 1
            : APP_HEADER,
          boxShadow: isCollapsed ? 'none' : '4px 0 16px rgba(0,0,0,0.15)',
          transition: 'left 0.25s ease',
        },
      }}
    >
      {/* Nav items — on mobile/tablet overlay, push below the fixed header */}
      <div
        css={{
          flex: 1,
          overflow: 'hidden',
          '@media (max-width: 1300px)': {
            paddingTop: 'var(--carrier-header-height, 65px)',
          },
          marginTop: '10px',
        }}
      >
        {/* Search input for tablet and mobile view */}
        {showSearchBar &&
          !isEmpty(customerList) &&
          (enableCarrierPortalResponsiveness
            ? tabletResp || mobileResp
            : mobile) && (
            <>
              <GlobalSearch onClose={onClose} />
              <hr
                css={{
                  margin: '0 10px 10px',
                  color: gray[95],
                }}
              />
            </>
          )}

        {links.map((link) => {
          const isActive = isActiveLink(link.path);
          return (
            <Tooltip
              key={link.testId}
              label={isCollapsed && desktopResp ? link.label : undefined}
              positionType="bottom"
            >
              {/*
                Tooltip attaches its floating-ui reference ref to this element via
                cloneElement. `Link` is a plain function component (not wrapped in
                forwardRef), so the ref silently fails to attach when Link is the
                direct child — Tooltip then measures a null/zero-rect reference and
                renders the tooltip at (0, 0). Wrapping Link in a plain host `div`
                gives Tooltip a real DOM node to measure and position against.
              */}
              <div css={{ margin: '0 10px' }}>
                <Link
                  to={link.path as RoutePath<`${string}/`, `${string}/`>}
                  data-testid={link.testId}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (win.innerWidth <= 1300) {
                      onClose?.();
                    }
                  }}
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isCollapsed ? '14px 0' : '14px 20px',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    textDecoration: 'none',
                    color: `${isActive ? activeTextColor : textColor} !important`,
                    backgroundColor: isActive ? activeBg : 'transparent',
                    borderLeft: 'none',
                    borderRadius: '10px',
                    transition: 'background-color 0.15s ease',
                    ':hover': {
                      backgroundColor: isActive ? activeBg : hoverBg,
                    },
                  }}
                >
                  {link.icon && (
                    <Icon
                      i={link.icon}
                      size="md"
                      color="inherit"
                      css={{ flexShrink: 0 }}
                    />
                  )}
                  {!isCollapsed && (
                    <span
                      css={{
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {link.label}
                    </span>
                  )}
                </Link>
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Powered by MasterMind branding */}
      <div
        data-testid="sidebar-mastermind-branding"
        css={{
          borderTop: `1px solid ${borderColor}`,
          padding: isCollapsed ? '12px 0' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: '8px',
        }}
      >
        <Logo color={gray[10]} width="36px" css={{ flexShrink: 0 }} />
        {!isCollapsed && (
          <span
            css={{
              display: 'flex',
              flexDirection: 'column',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }}
          >
            <span css={{ fontSize: '10px', color: gray[40] }}>powered by</span>
            <span
              css={{
                fontSize: '13px',
                fontWeight: 700,
                color: gray[10],
                letterSpacing: '0.06em',
              }}
            >
              MASTERMIND
              <sup css={{ fontSize: '8px', verticalAlign: 'super' }}>®</sup>
            </span>
          </span>
        )}
      </div>
    </nav>
  );
};
